import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function DokumentAnsichtPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url = null;
    (async () => {
      try {
        const [{ data: meta }, blobRes] = await Promise.all([
          api.get(`/documents/${id}`),
          api.get(`/documents/${id}/download`, { responseType: "blob" }),
        ]);
        setDoc(meta);
        url = URL.createObjectURL(blobRes.data);
        setBlobUrl(url);
      } catch (e) { toast.error(errMsg(e)); nav("/portal"); }
      finally { setLoading(false); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id, nav]);

  const download = () => {
    if (!blobUrl || !doc) return;
    const a = document.createElement("a");
    a.href = blobUrl; a.download = doc.dateiname || "dokument";
    a.click();
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!doc) return null;

  const isPdf = (doc.mime || "").includes("pdf");
  const isImage = (doc.mime || "").startsWith("image/");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={() => nav("/portal")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
        </Button>
        <Button onClick={download} data-testid="dok-view-download">
          <Download className="h-4 w-4 mr-2" /> Herunterladen
        </Button>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-primary" /> {doc.titel}
          </CardTitle>
          {doc.beschreibung && (
            <p className="text-sm text-muted-foreground">{doc.beschreibung}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {doc.dateiname} · {(doc.groesse_bytes / 1024).toFixed(1)} KB · veröffentlicht am {fmtDate((doc.erstellt_am || "").slice(0, 10))}
          </p>
        </CardHeader>
        <CardContent>
          {isPdf ? (
            <iframe title={doc.titel} src={blobUrl}
              className="w-full rounded-md border" style={{ height: "75vh" }} />
          ) : isImage ? (
            <div className="flex justify-center bg-secondary rounded-md p-4">
              <img src={blobUrl} alt={doc.titel} className="max-w-full max-h-[70vh] object-contain rounded" />
            </div>
          ) : (
            <div className="rounded-md border bg-secondary/40 p-8 text-center space-y-2">
              <FileText className="h-10 w-10 text-muted-foreground mx-auto" />
              <p className="text-sm">Diese Datei kann im Browser nicht direkt angezeigt werden.</p>
              <Button onClick={download}><Download className="h-4 w-4 mr-2" /> Herunterladen</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
